// Blog article images
import organisationImg from "@/assets/blog/organisation-documents.jpg";
import rechercheIaImg from "@/assets/blog/recherche-ia.jpg";
import securiteImg from "@/assets/blog/securite-documents.jpg";
import ocrImg from "@/assets/blog/ocr-reconnaissance.jpg";
import freelanceImg from "@/assets/blog/freelance-documents.jpg";
import partageImg from "@/assets/blog/partage-securise.jpg";

export interface BlogArticle {
  id: string;
  slug: string;
  title: string;
  description: string;
  content: string;
  author: string;
  publishedAt: string;
  readTime: string;
  category: string;
  tags: string[];
  image: string;
}

export const blogArticles: BlogArticle[] = [
  {
    id: "1",
    slug: "5-conseils-organiser-documents-numeriques",
    title: "5 Conseils pour Organiser vos Documents Numériques Efficacement",
    description: "Découvrez les meilleures pratiques pour classer et retrouver facilement vos documents importants. Une organisation efficace peut vous faire gagner des heures chaque mois.",
    content: `
## Pourquoi l'organisation documentaire est essentielle

Dans notre ère numérique, nous accumulons des centaines de documents chaque année : factures, contrats, attestations, relevés bancaires... Sans système d'organisation, retrouver un document spécifique devient rapidement un cauchemar.

### 1. Adoptez une nomenclature cohérente

La première étape vers une organisation efficace est d'adopter une convention de nommage claire pour tous vos fichiers. Un format recommandé : **Type - Entité - Date**.

Par exemple :
- Facture - EDF - 2024-01
- Contrat - Orange - 2023-12
- Attestation - AXA - 2024

### 2. Créez une structure de dossiers logique

Organisez vos documents par catégorie plutôt que par date :
- **Logement** : bail, quittances, diagnostics
- **Véhicule** : carte grise, assurance, contrôle technique
- **Santé** : ordonnances, analyses, remboursements
- **Travail** : bulletins de salaire, contrats, attestations

### 3. Numérisez vos documents papier

Ne gardez pas de documents importants uniquement en version papier. Utilisez un scanner ou une application mobile pour les numériser et les sauvegarder. L'OCR (reconnaissance optique de caractères) permet ensuite de rechercher dans le contenu de ces documents.

### 4. Utilisez des tags et des métadonnées

Les tags permettent de retrouver un document selon plusieurs critères. Un même document peut être tagué "impôts", "2024" et "urgent" par exemple.

### 5. Automatisez avec l'IA

Les outils modernes comme EasyFinder utilisent l'intelligence artificielle pour :
- Classer automatiquement vos documents
- Extraire les informations clés (dates, montants, entités)
- Permettre la recherche en langage naturel

## Conclusion

Une bonne organisation documentaire vous fera gagner du temps et réduira votre stress lors de la recherche de documents importants. Commencez dès aujourd'hui !
    `,
    author: "EasyFinder",
    publishedAt: "2024-12-15",
    readTime: "5 min",
    category: "Organisation",
    tags: ["organisation", "productivité", "documents numériques"],
    image: organisationImg
  },
  {
    id: "2",
    slug: "recherche-ia-documents-revolution",
    title: "Comment l'IA Révolutionne la Recherche de Documents",
    description: "L'intelligence artificielle transforme notre façon de retrouver nos documents. Découvrez comment la recherche en langage naturel change la donne.",
    content: `
## La fin des recherches par mots-clés

Pendant des années, retrouver un document signifiait se souvenir de son nom exact ou parcourir des dizaines de dossiers. L'intelligence artificielle change complètement cette approche.

### La recherche en langage naturel

Imaginez pouvoir demander à votre ordinateur : "Trouve ma déclaration d'impôts de l'année dernière" ou "Où est le devis du plombier qui est venu l'été dernier ?"

C'est exactement ce que permet la recherche sémantique propulsée par l'IA. Au lieu de chercher des correspondances exactes de mots-clés, le système comprend le sens de votre requête.

### Comment ça fonctionne ?

1. **Embeddings vectoriels** : Chaque document est converti en une représentation mathématique qui capture son sens
2. **Compréhension contextuelle** : L'IA comprend que "avis d'imposition" et "déclaration de revenus" sont liés
3. **Extraction d'entités** : Dates, montants, noms d'entreprises sont automatiquement identifiés

### Les avantages concrets

- **Gain de temps** : Trouvez n'importe quel document en quelques secondes
- **Pas besoin de mémoriser** : Décrivez simplement ce que vous cherchez
- **Découverte intelligente** : L'IA peut suggérer des documents connexes

### L'importance de la qualité des données

Pour que l'IA soit efficace, vos documents doivent être correctement numérisés et indexés. L'OCR (reconnaissance de caractères) transforme les scans en texte recherchable.

## L'avenir de la gestion documentaire

L'IA ne fait que commencer à transformer notre rapport aux documents. Bientôt, votre assistant virtuel pourra anticiper vos besoins et vous rappeler les échéances importantes automatiquement.
    `,
    author: "EasyFinder",
    publishedAt: "2024-12-10",
    readTime: "4 min",
    category: "Intelligence Artificielle",
    tags: ["IA", "recherche", "innovation"],
    image: rechercheIaImg
  },
  {
    id: "3",
    slug: "securite-documents-numeriques-bonnes-pratiques",
    title: "Sécurité des Documents Numériques : Les Bonnes Pratiques",
    description: "Protégez vos documents sensibles avec ces conseils de sécurité essentiels. De la sauvegarde au chiffrement, tout ce que vous devez savoir.",
    content: `
## Pourquoi la sécurité documentaire est cruciale

Vos documents contiennent des informations sensibles : numéros de carte d'identité, informations bancaires, données fiscales... Une fuite de ces données peut avoir des conséquences graves.

### Les menaces principales

- **Ransomware** : Logiciels qui chiffrent vos fichiers et demandent une rançon
- **Phishing** : Tentatives d'obtenir vos identifiants par tromperie
- **Perte de données** : Panne matérielle, vol, ou suppression accidentelle

### Bonnes pratiques de sécurité

#### 1. Sauvegarde régulière

Appliquez la règle 3-2-1 :
- **3** copies de vos données
- **2** types de supports différents
- **1** copie hors site (cloud sécurisé)

#### 2. Chiffrement des données

Vos documents sensibles doivent être chiffrés :
- Au repos (sur le disque dur)
- En transit (lors des transferts)

#### 3. Mots de passe robustes

Utilisez des mots de passe uniques et complexes pour chaque service. Un gestionnaire de mots de passe est fortement recommandé.

#### 4. Authentification à deux facteurs

Activez la 2FA sur tous vos comptes importants. Même si votre mot de passe est compromis, vos données restent protégées.

### Choisir un service de stockage sécurisé

Vérifiez que votre solution de gestion documentaire offre :
- Chiffrement AES-256
- Authentification forte
- Conformité RGPD
- Serveurs en Europe

## Conclusion

La sécurité de vos documents n'est pas optionnelle. Prenez le temps de mettre en place ces mesures pour protéger vos informations les plus précieuses.
    `,
    author: "EasyFinder",
    publishedAt: "2024-12-05",
    readTime: "6 min",
    category: "Sécurité",
    tags: ["sécurité", "RGPD", "chiffrement", "bonnes pratiques"],
    image: securiteImg
  },
  {
    id: "4",
    slug: "ocr-reconnaissance-caracteres-guide-complet",
    title: "OCR : Tout Comprendre sur la Reconnaissance de Caractères",
    description: "L'OCR transforme vos documents scannés en texte recherchable. Découvrez comment cette technologie fonctionne et ses applications pratiques.",
    content: `
## Qu'est-ce que l'OCR ?

L'OCR (Optical Character Recognition ou Reconnaissance Optique de Caractères) est une technologie qui permet de convertir des images de texte en texte éditable et recherchable.

### Comment fonctionne l'OCR ?

Le processus se déroule en plusieurs étapes :

1. **Prétraitement de l'image** : Amélioration du contraste, correction de l'inclinaison, suppression du bruit
2. **Segmentation** : Identification des zones de texte, des lignes, des mots et des caractères
3. **Reconnaissance** : Chaque caractère est comparé à une base de données de formes connues
4. **Post-traitement** : Correction orthographique et contextuelle

### Les avancées de l'IA

L'OCR moderne utilise le deep learning pour :
- Reconnaître l'écriture manuscrite
- Gérer les documents de mauvaise qualité
- Comprendre la structure des documents (tableaux, colonnes)
- Extraire des informations spécifiques (dates, montants, noms)

### Applications pratiques

#### Numérisation de documents

Transformez vos archives papier en documents numériques recherchables.

#### Extraction automatique de données

L'OCR intelligent peut extraire automatiquement :
- Les montants sur les factures
- Les dates d'échéance
- Les noms des parties sur les contrats

#### Accessibilité

Les documents scannés deviennent accessibles aux lecteurs d'écran pour les personnes malvoyantes.

### Conseils pour de meilleurs résultats

- Utilisez un scanner de qualité (300 DPI minimum)
- Assurez un bon éclairage
- Évitez les documents froissés ou pliés
- Préférez le scan au format PDF/A

## Conclusion

L'OCR est une technologie mature et indispensable pour une gestion documentaire efficace. Combinée à l'IA, elle ouvre la voie à une organisation entièrement automatisée de vos documents.
    `,
    author: "EasyFinder",
    publishedAt: "2024-11-28",
    readTime: "5 min",
    category: "Technologie",
    tags: ["OCR", "numérisation", "technologie"],
    image: ocrImg
  },
  {
    id: "5",
    slug: "gestion-documents-freelance-independant",
    title: "Gestion Documentaire pour Freelances et Indépendants",
    description: "En tant qu'indépendant, la gestion administrative peut être chronophage. Voici comment optimiser votre organisation documentaire.",
    content: `
## Les défis des indépendants

Les freelances et entrepreneurs jonglent avec de nombreux documents : devis, factures, contrats clients, justificatifs de frais, documents comptables... Sans organisation rigoureuse, c'est vite le chaos.

### Les documents essentiels à conserver

#### Documents commerciaux
- Devis et propositions commerciales
- Factures émises et reçues
- Bons de commande
- Contrats clients

#### Documents comptables
- Relevés bancaires professionnels
- Justificatifs de frais
- Déclarations TVA
- Bilans et comptes de résultat

#### Documents administratifs
- Immatriculation (KBIS, URSSAF)
- Attestations d'assurance RC Pro
- Certifications et agréments

### Durée de conservation légale

- **Factures** : 10 ans
- **Contrats commerciaux** : 5 ans après la fin du contrat
- **Documents comptables** : 10 ans
- **Bulletins de salaire** (si employeur) : 5 ans

### Conseils d'organisation

#### 1. Séparez pro et perso

Créez une structure distincte pour vos documents professionnels. Ne mélangez jamais avec vos documents personnels.

#### 2. Numérisez immédiatement

Dès réception d'un document papier (ticket de caisse, facture), numérisez-le. Un scan de mauvaise qualité vaut mieux que pas de scan du tout.

#### 3. Automatisez la classification

Utilisez un outil avec classification automatique pour gagner du temps. L'IA peut identifier un devis d'une facture instantanément.

#### 4. Préparez vos échéances fiscales

Tagguez vos documents par année fiscale pour faciliter les déclarations et les éventuels contrôles.

### L'avantage de la recherche intelligente

En tant qu'indépendant, vous avez souvent besoin de retrouver rapidement :
- "Le devis envoyé au client X en mars"
- "Toutes les factures fournisseur de 2024"
- "Les justificatifs de frais de déplacement du dernier trimestre"

Une recherche en langage naturel vous fait gagner un temps précieux.

## Conclusion

Une bonne organisation documentaire est un investissement qui rapporte : moins de stress, gain de temps, et sérénité face aux obligations légales.
    `,
    author: "EasyFinder",
    publishedAt: "2024-11-20",
    readTime: "7 min",
    category: "Productivité",
    tags: ["freelance", "indépendant", "comptabilité", "organisation"],
    image: freelanceImg
  },
  {
    id: "6",
    slug: "partage-securise-documents-collaborateurs",
    title: "Comment Partager vos Documents en Toute Sécurité",
    description: "Le partage de documents est essentiel pour la collaboration. Découvrez les meilleures pratiques pour partager sans compromettre la sécurité.",
    content: `
## L'importance du partage sécurisé

Que ce soit avec un comptable, un avocat, un client ou un collaborateur, nous avons régulièrement besoin de partager des documents sensibles. Un partage mal sécurisé peut avoir des conséquences désastreuses.

### Les risques du partage non sécurisé

- **Interception** : Un document envoyé par email peut être intercepté
- **Accès non autorisé** : Un lien partagé peut être transmis à des tiers
- **Perte de contrôle** : Une fois envoyé, vous ne contrôlez plus le document

### Solutions de partage sécurisé

#### 1. Liens avec expiration

Créez des liens de partage qui expirent automatiquement après une durée définie ou un nombre de consultations limité.

#### 2. Protection par mot de passe

Ajoutez une couche de sécurité supplémentaire en protégeant vos liens par mot de passe. Communiquez le mot de passe par un autre canal (SMS, appel).

#### 3. Permissions granulaires

Définissez précisément ce que le destinataire peut faire :
- **Lecture seule** : Consultation uniquement
- **Commentaires** : Possibilité d'annoter le document
- **Téléchargement** : Autoriser ou non la copie locale

#### 4. Traçabilité

Conservez un historique des accès : qui a consulté le document, quand, et depuis quelle IP.

### Cas d'usage courants

#### Partage avec votre comptable
Créez un dossier partagé avec toutes vos factures du trimestre. Le lien avec permission "lecture seule" suffit généralement.

#### Envoi de devis à un client
Partagez votre devis avec une date d'expiration correspondant à la validité du devis. Activez les commentaires pour permettre les retours.

#### Documents pour un prêt bancaire
Regroupez tous les documents demandés dans un dossier unique. Protégez par mot de passe vu la sensibilité des informations.

### Bonnes pratiques

- Ne partagez jamais de documents sensibles par email sans chiffrement
- Révoquez les accès dès qu'ils ne sont plus nécessaires
- Utilisez des canaux différents pour le lien et le mot de passe
- Vérifiez régulièrement les partages actifs

## Conclusion

Le partage sécurisé n'est pas compliqué une fois les bonnes habitudes prises. Protégez vos données et celles de vos interlocuteurs.
    `,
    author: "EasyFinder",
    publishedAt: "2024-11-15",
    readTime: "5 min",
    category: "Sécurité",
    tags: ["partage", "collaboration", "sécurité"],
    image: partageImg
  }
];

export const getBlogArticleBySlug = (slug: string): BlogArticle | undefined => {
  return blogArticles.find(article => article.slug === slug);
};

export const getRelatedArticles = (currentSlug: string, limit: number = 3): BlogArticle[] => {
  const currentArticle = getBlogArticleBySlug(currentSlug);
  if (!currentArticle) return blogArticles.slice(0, limit);
  
  return blogArticles
    .filter(article => article.slug !== currentSlug)
    .filter(article => 
      article.category === currentArticle.category ||
      article.tags.some(tag => currentArticle.tags.includes(tag))
    )
    .slice(0, limit);
};
